import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import SVG from "react-inlinesvg";
import BottomCaretIcon from "buba-components/src/assets/icons/other/bottomCaret.svg";
import ClearIcon from "buba-components/src/assets/icons/other/x.svg";
import cx from "classnames";
import isEqual from "lodash/isEqual";
import { matchSorter } from "match-sorter";

import type { InputType, MetaType } from "../Input/TextInput";
import TextInput from "../Input/TextInput";
import Popover, { duration as popoverAnimationDuration } from "../Popover";
import { PopoverRenderProps } from "../Popover/Popover";

import styles from "./Select.scss";

export interface SelectOption {
  value: string;
  labelOnSelected?: string;
  label: string;
  searchString?: string;
}

interface SelectProps {
  placeholder?: string;
  options: Array<SelectOption>;
  input: InputType;
  meta: MetaType;
  autoComplete?: boolean;
}

const Select = ({ input, meta, options = [], placeholder, autoComplete = true }: SelectProps) => {
  const labelIndex = options.findIndex(x => x.value === input?.value);
  const [typedValue, setTypedValue] = useState<string | undefined>(
    options[labelIndex]?.label || input?.value || ""
  );
  const [isTouched, setIsTouched] = useState<boolean>(false);

  const optionRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);

  const isValueSelected = useCallback(
    (valueToCheck: string) => {
      return isEqual(valueToCheck, input.value);
    },
    [input]
  );

  const handleArrowNavigation = (e: KeyboardEvent<HTMLElement>) => {
    if (!(e.target instanceof HTMLElement)) return;
    const { activeElement } = document;
    const shouldHandleKeyDown = [e.target, activeElement].every(
      el =>
        el instanceof Node &&
        optionRef &&
        optionRef.current &&
        optionRef.current.contains &&
        optionRef.current.contains(el)
    );

    if (shouldHandleKeyDown) {
      let nextFocusedElement = null;

      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          nextFocusedElement = activeElement && activeElement.previousSibling;
          break;
        case "ArrowDown":
          e.preventDefault();
          nextFocusedElement = activeElement && activeElement.nextSibling;
          break;
        default:
      }
      if (nextFocusedElement && nextFocusedElement instanceof HTMLElement) {
        nextFocusedElement.focus();
      }
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleArrowNavigation as unknown as EventListener);

    return () => {
      document.removeEventListener("keydown", handleArrowNavigation as unknown as EventListener);
    };
  }, []);

  const handleInputValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsTouched(true);
    if (e.target instanceof HTMLInputElement) {
      const { value } = e.target;
      if (typeof value === "string") {
        setTypedValue(value);
      }
    }
  };

  const handleSelect = (option: SelectOption, closePopover: () => void) => {
    closePopover();
    input.onChange(option?.value);
    // remove it after animation is completed, to avoid flickering
    setTimeout(() => {
      setTypedValue(option.labelOnSelected || option.label);
    }, popoverAnimationDuration);
  };

  const getOptionsToRender = () => {
    if (!autoComplete) {
      return options;
    }

    return matchSorter(options, String(typedValue), {
      keys: ["label", "labelOnSelected", "searchString"]
    });
  };

  const renderItems = (closePopover: () => void): React.ReactNode => {
    const view = getOptionsToRender();

    if (!view.length) {
      return isTouched ? <div className={styles.notFound}>Keine Ergebnisse</div> : <div />;
    }
    return view.map((option: SelectOption, optionIndex: number) => (
      <button
        type="button"
        role="option"
        className={cx(styles.option, {
          [styles.optionSelected]: isValueSelected(option?.value)
        })}
        // eslint-disable-next-line react/no-array-index-key
        key={`${option.label}${optionIndex}`}
        onClick={() => {
          handleSelect(option, closePopover);
        }}
        data-closeontab={optionIndex === view.length}
        aria-label={option.label}
        aria-selected={isValueSelected(option?.value)}
        data-testid={`option:${input.name}-${option?.value}`}
      >
        {option.label}
      </button>
    ));
  };

  const clearValue = (closePopover: () => void) => {
    setTypedValue("");
    input.onChange(undefined);
    closePopover();

    const textInput = textInputRef.current;
    if (textInput) {
      textInput.blur();
    }
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>, closePopover: () => void) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();

      if (optionRef.current && optionRef.current.children.length) {
        (optionRef.current.children[0] as HTMLElement).focus();
      }
    } else if (e.key === "Backspace") {
      const shouldClearValue = input?.value;

      if (shouldClearValue) {
        clearValue(closePopover);
      }
    }
  };

  const renderSideSymbol = (closePopover: () => void) => {
    if (input?.value) {
      return (
        <button
          type="button"
          className={styles.clear}
          onClick={() => clearValue(closePopover)}
          data-testid="clear"
        >
          <SVG src={ClearIcon} className={cx(styles.sideSymbol)} />
        </button>
      );
    }

    return <SVG src={BottomCaretIcon} className={cx(styles.sideSymbol)} />;
  };

  const renderInput = (inputProps: PopoverRenderProps) => {
    return (
      <TextInput
        input={input}
        meta={meta}
        sideSymbol={() => renderSideSymbol(inputProps.closePopover)}
        onChange={handleInputValueChange}
        value={typedValue}
        onFocus={(e: FocusEvent<HTMLInputElement>) => {
          input.onFocus(e);
          if (inputProps.onFocus) {
            inputProps.onFocus(e);
          }
        }}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          setIsTouched(false);
          input.onBlur(e);
        }}
        data-closeonshifttab
        placeholder={placeholder}
        readOnly={!autoComplete}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
          handleInputKeyDown(e, inputProps.closePopover)
        }
        data-testid={`selectInput:${input.name}`}
        data-name={input.name}
        innerRef={textInputRef as MutableRefObject<HTMLInputElement>}
      />
    );
  };

  const handleClosePopover = () => {
    setTypedValue(typedValue);
  };

  const renderContent = (closePopover: () => void) => {
    return renderItems(closePopover);
  };

  return (
    <Popover
      input={renderInput}
      content={({ closePopover }) => (
        <div ref={optionRef} className={cx(styles.optionsWrapper)} role="listbox">
          {renderContent(closePopover)}
        </div>
      )}
      onClose={handleClosePopover}
    />
  );
};

export default Select;
